import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

import { PageHeader } from '@/components/PageHeader';
import { DataState } from '@/components/DataState';
import { useApiQuery } from '@/hooks/useApiQuery';
import { commerce } from '@/api/endpoints';
import { formatDate, formatPrice } from '@/utils/format';

/**
 * Memberships & packages.
 *
 * Patients see two parallel things:
 *   1. Active care packages they bought (one-shot bundle, depletes per visit).
 *   2. Active membership subscriptions (recurring).
 *
 * Buying / cancelling lives on dedicated commerce pages (Tour 4+); this
 * page is read-only.
 */

type Package = {
  id: number;
  package: { name: string };
  status: 'active' | 'expired' | 'used_up' | 'refunded';
  purchased_at: string;
  expires_at: string | null;
  price_amount: string;
  price_currency: string;
  consumption_state: Record<string, number>;
};
type Subscription = {
  id: string;
  plan: { name: string; monthly_price_amount: string; price_currency: string };
  status: 'trialing' | 'active' | 'past_due' | 'cancelled' | 'expired';
  current_period_end: string;
};

export function PatientSubscriptionsPage() {
  const packagesQ = useApiQuery({
    queryKey: ['patient', 'commerce', 'packages'],
    queryFn:  commerce.packages.list as unknown as () => Promise<Package[]>,
  });
  const subsQ = useApiQuery({
    queryKey: ['patient', 'commerce', 'memberships'],
    queryFn:  commerce.memberships.list as () => Promise<Subscription[]>,
  });

  return (
    <>
      <PageHeader title="Memberships & packages" />

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}>
            Subscriptions
          </Typography>
          <DataState
            query={subsQ}
            empty={{
              title: 'No active subscriptions',
              description: 'Memberships give you discounted or free visits at participating practices.',
            }}
          >
            {(subs) => (
              <Stack spacing={1.5}>
                {subs.map((s) => (
                  <Card key={s.id}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                          width: 40, height: 40, borderRadius: 1.5,
                          bgcolor: 'background.subtle', color: 'primary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <SubscriptionsIcon />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center"
                                 sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {s.plan.name}
                            </Typography>
                            <Chip size="small"
                                  label={s.status}
                                  color={s.status === 'active' || s.status === 'trialing'
                                         ? 'success' : 'default'}
                                  variant="outlined" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary"
                                      className="tabular">
                            {formatPrice(s.plan.monthly_price_amount, s.plan.price_currency)} / month
                            · renews {formatDate(s.current_period_end)}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </DataState>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="overline" color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}>
            Care packages
          </Typography>
          <DataState
            query={packagesQ}
            empty={{
              title: 'No care packages',
              description: 'Care packages bundle a defined number of visits or tests at a fixed price.',
            }}
          >
            {(pkgs) => (
              <Stack spacing={1.5}>
                {pkgs.map((p) => (
                  <Card key={p.id}>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                          width: 40, height: 40, borderRadius: 1.5,
                          bgcolor: 'background.subtle', color: 'primary.main',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <Inventory2Icon />
                        </Box>
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center"
                                 sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600} noWrap>
                              {p.package.name}
                            </Typography>
                            <Chip size="small"
                                  label={p.status.replace(/_/g, ' ')}
                                  color={p.status === 'active' ? 'success' : 'default'}
                                  variant="outlined" />
                          </Stack>
                          <Typography variant="body2" color="text.secondary"
                                      className="tabular">
                            {formatPrice(p.price_amount, p.price_currency)}
                            {p.expires_at && ` · expires ${formatDate(p.expires_at)}`}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </DataState>
        </Grid>
      </Grid>
    </>
  );
}
